import { computed, observable } from 'mobx'
import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { unixEpoch } from '@/utils/date-util'
import type { Member } from '@/features/iam/members/Member'
import type { LegendServiceType } from '@/features/jobs/legends/legend-service-type'
import { LegendVersionId } from '@/features/jobs/legends/PublishedLegend'
import type { LegendDocument } from '@/features/legends/editor/legend-document'

export type LegendVersionProps = ModelProps<LegendVersion>

/**
 * A version of a legend: a draft, the published one, or an older one.
 */
export class LegendVersion extends Model<LegendVersion> {
  /**
   * The legend's ID, which its versions share; {@link versionId} tells them
   * apart.
   */
  id = Id.none

  @observable accessor version = 0
  @observable accessor workspaceId = Id.none
  @observable accessor concurrencyVersion = 0
  @observable accessor name = ''
  @observable accessor description = ''
  @observable accessor serviceType: LegendServiceType | null = null
  @observable accessor published = false
  @observable accessor dateModified = unixEpoch()
  @observable accessor datePublished: Date | null = null
  @observable accessor versionNotes = ''
  @observable accessor viewOrder = 0
  @observable accessor lastUpdatedBy: Member | null = null

  /**
   * The version's content; `null` until the version itself is fetched, since
   * version listings leave it out.
   */
  @observable.ref accessor document: LegendDocument | null = null

  /**
   * Tells the version apart from the legend's other versions.
   */
  @computed
  get versionId(): LegendVersionId {
    return Id.some(this.id)
      ? LegendVersionId(this.id, this.version)
      : LegendVersionId.none
  }
}
